import React from "react";

import store from "app/store";
import { Modal, Form, FormMessage, Button, Dimmer, Loader, FormControl } from "lib/components/index";
import st from "lib/utils/index";
import { ProductsRepository } from "lib/repositories/index";
import { ProductFullDecorator, ProductVariantFullDecorator } from "lib/decorators/index";
import { debounce } from "lodash";

const COPY_PROPS = [
  {
    prop: "copyDescription",
    label: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_description_label"),
    help: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_description_hint"),
  },
  {
    prop: "copyPricelist",
    label: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_pricelist_label"),
    help: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_pricelist_hint"),
  },
  {
    prop: "copyPayments",
    label: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_payments_label"),
    help: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_payments_hint"),
  },
  {
    prop: "copyBookingSettings",
    label: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_booking_settings_label"),
    help: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_booking_settings_hint"),
  },
  {
    prop: "copyPickupPoints",
    label: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_pickup_points_label"),
    help: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_pickup_points_hint"),
  },
  {
    prop: "copyPricedFields",
    label: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_priced_fields_label"),
    help: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_priced_fields_hint"),
  },
  {
    prop: "copyCustomerFields",
    label: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_customer_fields_label"),
    help: I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_customer_fields_hint"),
  },
];

function copyProductAttributes({
  sourceProduct: sourceP,
  sourceProductVariant: sourcePv,
  targetProduct: targetP,
  targetProductVariant: targetPv,
  copyDescription,
  copyPricelist,
  copyPayments,
  copyBookingSettings,
  copyPickupPoints,
  copyPricedFields,
  copyCustomerFields,
}) {
  if (!sourceP || !sourcePv || !targetP || !targetPv) {
    throw new Error("One of the main arguments are missing");
  }
  const nextTargetP = new ProductFullDecorator({
    ...targetP,
    product_variants: targetP.product_variants.map((pv) => (pv.uuid === targetPv.uuid ? targetPv : pv)),
  });
  const nextTargetPv = nextTargetP.product_variants.find((pv) => pv.uuid === targetPv.uuid);

  if (copyDescription) {
    nextTargetPv.translations = targetPv.translations.map((prevT) => {
      const nextT = sourcePv.translations.find((t) => t.locale === prevT.locale);
      return nextT
        ? {
            ...nextT,
            id: prevT.id,
            uuid: prevT.uuid,
            name: targetP.product_variants.length > 1 ? nextT.name : null,
          }
        : prevT;
    });
    // Fix for copy description
    nextTargetP.translations = targetP.translations.map((prevT) => {
      const nextT = sourceP.translations.find((t) => t.locale === prevT.locale);
      return nextT
        ? {
            ...nextT,
            id: prevT.id,
            uuid: prevT.uuid,
            name: targetP.product_variants.length > 1 ? nextT.name : null,
          }
        : prevT;
    });
  }

  if (copyPricelist) {
    nextTargetP.price_currency = sourceP.price_currency;
    nextTargetPv.pricelist_type = sourcePv.pricelist_type;
    nextTargetPv.product_pricelist_items = sourcePv.product_pricelist_items.map((ppi) => ({
      ...ppi,
      id: undefined,
      uuid: st.generateUuid(),
    }));
  }

  if (copyPayments) {
    nextTargetPv.payment_profile_product_attachments = sourcePv.payment_profile_product_attachments.map((pppa) => {
      const existingPppa = nextTargetPv.payment_profile_product_attachments.find(
        (a) => a.payment_profile_id === pppa.payment_profile_id
      );
      const id = existingPppa ? existingPppa.id : null;
      const uuid = existingPppa ? existingPppa.uuid : st.generateUuid();
      return { ...pppa, id, uuid };
    });
  }

  if (copyBookingSettings) {
    nextTargetPv.nb_total_stock_limit = sourcePv.nb_total_stock_limit;
    nextTargetPv.nb_stock_useby_days_limit = sourcePv.nb_stock_useby_days_limit;
    nextTargetPv.nb_stock_useby_starts_at = sourcePv.nb_stock_useby_starts_at;
    nextTargetPv.nb_stock_useby_ends_at = sourcePv.nb_stock_useby_ends_at;
    nextTargetPv.nb_requires_company_approval = sourcePv.nb_requires_company_approval;
    nextTargetPv.default_total_stock_limit = sourcePv.default_total_stock_limit;
    nextTargetPv.default_requires_company_approval = sourcePv.default_requires_company_approval;
  }

  if (copyPickupPoints) {
    nextTargetPv.product_pickup_points = sourcePv.product_pickup_points.map((ppp) => ({
      ...ppp,
      id: undefined,
      uuid: st.generateUuid(),
    }));
    nextTargetPv.allow_no_pickup_point = sourcePv.allow_no_pickup_point;
    nextTargetPv.allow_custom_pickup_point = sourcePv.allow_custom_pickup_point;
    nextTargetPv.custom_pickup_point_additional_fee = sourcePv.custom_pickup_point_additional_fee;
    nextTargetPv.translations = nextTargetPv.translations.map((prevT) => {
      const nextT = sourcePv.translations.find((t) => t.locale === prevT.locale);
      return nextT
        ? {
            ...prevT,
            additional_pickup_instructions: nextT.additional_pickup_instructions,
          }
        : prevT;
    });
  }

  if (copyPickupPoints) {
    nextTargetP.pickup_points_assignation_type = sourceP.pickup_points_assignation_type;
    nextTargetP.pickup_points_price_type = sourceP.pickup_points_price_type;
  }

  if (copyPricedFields) {
    nextTargetP.priced_fields = sourceP.priced_fields.map((pof) => ({
      ...pof,
      id: undefined,
      uuid: st.generateUuid(),
      product_variants_assignation_type: "product_variants_assignation_all",
      list_options:
        pof.list_options &&
        pof.list_options.map((lo) => ({
          ...lo,
          id: null,
          uuid: st.generateUuid(),
        })),
    }));
  }

  if (copyCustomerFields) {
    nextTargetP.customer_fields = sourceP.customer_fields.map((pof) => ({
      ...pof,
      id: undefined,
      uuid: st.generateUuid(),
      product_variants_assignation_type: "product_variants_assignation_all",
      list_options:
        pof.list_options &&
        pof.list_options.map((lo) => ({
          ...lo,
          id: null,
          uuid: st.generateUuid(),
        })),
    }));
  }

  return nextTargetP;
}

export class ProductVariantMigrateModal extends React.PureComponent {
  state = {
    products: [],
    sourceProductVariantId: null,
    copyDescription: true,
    copyPricelist: true,
    copyPayments: true,
    copyBookingSettings: true,
    copyPickupPoints: true,
    copyPricedFields: true,
    copyCustomerFields: true,
    fetchDone: false,
    fetchLoading: true,
    fetchFailed: false,
    submitLoading: false,
    submitFailed: false,
    error: null,
    search: "",
  };
  subscriptions = [];

  componentDidMount() {
    this.getProducts();
  }

  componentWillUnmount() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions.length = 0;
  }

  getProducts(params) {
    const findAllProducts$ = ProductsRepository.search(params)
      .map((products) =>
        products.records.filter(
          (p) => (st.authorize.member.st_admin ? true : p.company.id === st.authorize.company.id) // TODO: This can potentially by removed. Backend functionality duplication.
        )
      )
      .subscribe(
        (products) => {
          this.setState({
            products,
            fetchDone: true,
            fetchLoading: false,
            fetchFailed: false,
          });
        },
        (error) => {
          st.api.handleError(error, store);
          this.setState({
            fetchLoading: false,
            fetchFailed: true,
            error: st.api.findResponseErrorMessage(error),
          });
        }
      );
    this.subscriptions.push(findAllProducts$);
  }

  handleGetNewProducts = debounce((value) => {
    if (value.length >= 3) {
      this.getProducts({ search: value });
    }
  }, 400);

  handleSubmit(event) {
    event.preventDefault();
    const { targetProduct, targetProductVariant, resolve } = this.props;
    const { products, sourceProductVariantId } = this.state;
    if (!sourceProductVariantId || this.state.submitLoading) {
      return;
    }
    const sourceProduct = products.find((p) => p.product_variants.map((pv) => pv.id).includes(sourceProductVariantId));

    this.setState({ submitLoading: true, submitFailed: false }, () => {
      // TODO I want fresh response from server here, not the cached one
      this.subscriptions.push(
        ProductsRepository.find(sourceProduct.id)
          .take(1)
          .subscribe(
            (sourceProductFull) => {
              const sourceProductVariant = sourceProductFull.product_variants.find(
                (pv) => pv.id === sourceProductVariantId
              );
              if (!sourceProductVariant) {
                console.error(
                  `We couldn't find ProductVariant #${sourceProductVariantId} in given Product #${sourceProductFull.id}`
                );
                reject();
                return;
              }
              const nextTargetProduct = copyProductAttributes({
                sourceProduct: sourceProductFull,
                sourceProductVariant,
                targetProduct,
                targetProductVariant,
                copyDescription: this.state.copyDescription,
                copyPricelist: this.state.copyPricelist,
                copyPayments: this.state.copyPayments,
                copyBookingSettings: this.state.copyBookingSettings,
                copyPickupPoints: this.state.copyPickupPoints,
                copyPricedFields: this.state.copyPricedFields,
                copyCustomerFields: this.state.copyCustomerFields,
              });
              resolve(nextTargetProduct);
            },
            (error) => {
              st.api.handleError(error, store);
              this.setState({
                submitLoading: false,
                submitFailed: true,
                error: st.api.findResponseErrorMessage(error),
              });
            }
          )
      );
    });
  }

  handlePropChange(prop, value) {
    this.setState({ [prop]: value });
  }

  render() {
    const { modalRef, resolve, targetProduct, targetProductVariant, ...props } = this.props;
    const { products, sourceProductVariantId, fetchDone, fetchLoading, submitLoading, error } = this.state;

    const mapProductAndVariantToOption = ({ p, pv }) => [pv.id, [p.name, pv.name].filter((s) => !!s).join(" - ")];
    const sourcePvOptions = _(products)
      .map((p) => p.product_variants.map((pv) => ({ p, pv })))
      .flatten()
      .map(mapProductAndVariantToOption)
      .value();
    const targetPvOptions = [
      mapProductAndVariantToOption({
        p: targetProduct,
        pv: targetProductVariant,
      }),
    ];

    return (
      <Modal closeIcon closeOnDimmerClick={false} size="small" ref={modalRef} {...props}>
        <Modal.Header>{I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.title")}</Modal.Header>,
        <Modal.Content>
          <Dimmer inverted active={fetchLoading}>
            <Loader />
          </Dimmer>
          {!!error && <FormMessage error={error} />}
          {fetchDone && (
            <Form onSubmit={this.handleSubmit.bind(this)}>
              <FormControl.SelectFormGroup
                value={sourceProductVariantId}
                onChange={this.handlePropChange.bind(this, "sourceProductVariantId")}
                label={I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.source_variant")}
                options={sourcePvOptions.filter(([pvId, _]) => pvId !== targetProductVariant.id)}
                onInput={(e) => {
                  this.setState({ search: e.target.value });
                  this.handleGetNewProducts(e.target.value);
                }}
                search
                required
                autoFocus
              />

              <FormControl.SelectFormGroup
                value={targetProductVariant.id}
                label={I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.target_variant")}
                options={targetPvOptions}
                readOnly
              />

              <FormControl.FormGroup
                label={I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.copy_label")}
                grouped
              >
                {COPY_PROPS.map(({ prop, label, help }) => (
                  <FormControl.CheckboxFormGroup
                    key={prop}
                    value={this.state[prop]}
                    onChange={this.handlePropChange.bind(this, prop)}
                    label={label}
                    help={help}
                    toggle
                  />
                ))}
              </FormControl.FormGroup>

              <button type="submit" hidden disabled={!sourceProductVariantId || submitLoading} />
            </Form>
          )}
        </Modal.Content>
        ,
        <Modal.Actions>
          <Button as="a" onClick={() => resolve()} content={I18n.t("papp.common.cancel")} />
          <Button
            as="a"
            primary
            floated="right"
            disabled={!sourceProductVariantId || submitLoading}
            onClick={this.handleSubmit.bind(this)}
            content={I18n.t("papp.products.EditProductView.ProductVariantMigrateModal.submit")}
          />
        </Modal.Actions>
      </Modal>
    );
  }
}

export default function openProductVariantMigrateModal(props) {
  return st.createModal(ProductVariantMigrateModal, props);
}



// WEBPACK FOOTER //
// ./src/modules/products/EditProductView/StepDescription/ProductVariantMigrateModal.jsx